import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Trash2, Webhook, PlugZap } from 'lucide-react';
import { api } from '../../api/client';
import type { ChannelAuthMode, OwnerDetail as OwnerDetailType, PaymentChannelSummary } from '@pay/shared';

function feiltekst(err: unknown): string {
  return (err as { message?: string } | null)?.message ?? 'Noe gikk galt';
}

export default function OwnerDetail() {
  const { id } = useParams<{ id: string }>();
  const [owner, setOwner] = useState<OwnerDetailType | null>(null);
  const [channels, setChannels] = useState<PaymentChannelSummary[]>([]);
  const [savingLogo, setSavingLogo] = useState(false);

  const tomtSkjema = {
    msn: '',
    auth_mode: 'PARTNER' as ChannelAuthMode,
    client_id: '',
    client_secret: '',
    subscription_key: '',
    environment: 'prod' as 'test' | 'prod',
    allow_push: false,
    allow_qr: true,
  };
  const [vippsForm, setVippsForm] = useState(tomtSkjema);
  const [creatingVipps, setCreatingVipps] = useState(false);
  const [melding, setMelding] = useState<{ ok: boolean; tekst: string } | null>(null);

  function refresh() {
    if (!id) return;
    api.get<OwnerDetailType>(`/api/admin/owners/${id}`).then(setOwner).catch(() => {});
    api.get<PaymentChannelSummary[]>(`/api/admin/channels?owner_id=${id}`).then(setChannels).catch(() => {});
  }
  useEffect(refresh, [id]);

  async function uploadLogo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    setSavingLogo(true);
    const fd = new FormData();
    fd.append('file', file);
    await api.post(`/api/admin/owners/${id}/logo`, fd);
    setSavingLogo(false);
    refresh();
  }

  async function saveOwner(patch: Partial<OwnerDetailType>) {
    if (!owner || !id) return;
    await api.patch(`/api/admin/owners/${id}`, {
      name: owner.name,
      orgnr: owner.orgnr,
      contact_email: owner.contact_email,
      sender_name: owner.sender_name,
      sender_email: owner.sender_email,
      address: owner.address,
      reply_to: owner.reply_to,
      primary_color: owner.primary_color,
      default_flow: owner.default_flow,
      default_expires_in_minutes: owner.default_expires_in_minutes,
      external_key: owner.external_key ?? '',
      active: owner.active,
      ...patch,
    }).then(
      () => setMelding({ ok: true, tekst: 'Lagret' }),
      (err) => setMelding({ ok: false, tekst: feiltekst(err) }),
    );
    refresh();
  }

  async function addVipps(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    const body =
      vippsForm.auth_mode === 'PARTNER'
        ? {
            owner_id: id,
            msn: vippsForm.msn,
            auth_mode: 'PARTNER',
            environment: vippsForm.environment,
            allow_push: vippsForm.allow_push,
            allow_qr: vippsForm.allow_qr,
          }
        : { owner_id: id, ...vippsForm };
    try {
      await api.post('/api/admin/channels', body);
      setCreatingVipps(false);
      setVippsForm(tomtSkjema);
      setMelding({ ok: true, tekst: 'Avtalen er lagret. Test tilkoblingen og registrer webhook.' });
    } catch (err) {
      setMelding({ ok: false, tekst: feiltekst(err) });
    }
    refresh();
  }

  async function kanalHandling(tekstOk: string, kall: () => Promise<unknown>) {
    setMelding(null);
    try {
      await kall();
      setMelding({ ok: true, tekst: tekstOk });
    } catch (err) {
      setMelding({ ok: false, tekst: feiltekst(err) });
    }
    refresh();
  }

  async function deleteVipps(cid: string) {
    if (!confirm('Slette Vipps-avtale?')) return;
    await kanalHandling('Avtalen er slettet', () => api.delete(`/api/admin/channels/${cid}`));
  }

  if (!owner) return <div className="p-6">Laster …</div>;

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-2xl font-semibold mb-1">{owner.name}</h1>
      <div className="text-sm text-slate-500 mb-6">Org.nr {owner.orgnr}</div>

      <section className="bg-white rounded-lg shadow border border-slate-200 p-5 mb-5">
        <h2 className="font-semibold mb-4">Profil</h2>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Navn">
            <input className="input" value={owner.name} onChange={(e) => setOwner({ ...owner, name: e.target.value })} />
          </Field>
          <Field label="Kontakt-e-post">
            <input className="input" value={owner.contact_email} onChange={(e) => setOwner({ ...owner, contact_email: e.target.value })} />
          </Field>
          <Field label="Avsendernavn">
            <input className="input" value={owner.sender_name} onChange={(e) => setOwner({ ...owner, sender_name: e.target.value })} />
          </Field>
          <Field label="Avsender-e-post">
            <input className="input" value={owner.sender_email} onChange={(e) => setOwner({ ...owner, sender_email: e.target.value })} />
          </Field>
          <Field label="Reply-To">
            <input className="input" value={owner.reply_to ?? ''} onChange={(e) => setOwner({ ...owner, reply_to: e.target.value })} />
          </Field>
          <Field label="Primærfarge (hex)">
            <input className="input" value={owner.primary_color ?? ''} onChange={(e) => setOwner({ ...owner, primary_color: e.target.value })} placeholder="#1f2937" />
          </Field>
          <Field label="Standard flyt">
            <select className="input" value={owner.default_flow} onChange={(e) => setOwner({ ...owner, default_flow: e.target.value as 'PUSH' | 'CHECKOUT' | 'QR' })}>
              <option value="PUSH">PUSH (varsel i appen)</option>
              <option value="CHECKOUT">CHECKOUT (kasseside)</option>
              <option value="QR">QR (kunden skanner)</option>
            </select>
          </Field>
          <Field label="Ekstern nøkkel (kallesystem)">
            <input className="input font-mono" value={owner.external_key ?? ''} onChange={(e) => setOwner({ ...owner, external_key: e.target.value })} placeholder="else:82" />
            <span className="block text-xs text-slate-500 mt-1">ElseWeb-firma: else:&lt;firma-id&gt;. Kobler firmaet til denne eieren.</span>
          </Field>
          <Field label="Standard utløp (minutter)">
            <input className="input" type="number" value={owner.default_expires_in_minutes} onChange={(e) => setOwner({ ...owner, default_expires_in_minutes: Number(e.target.value) })} />
          </Field>
        </div>
        <div className="mt-4 flex items-center justify-end gap-3">
          {melding && <span className={melding.ok ? 'text-green-700 text-sm' : 'text-red-700 text-sm'}>{melding.tekst}</span>}
          <button onClick={() => saveOwner({})} className="bg-brand hover:bg-brand-dark text-white rounded px-3 py-2 text-sm font-medium">
            Lagre
          </button>
        </div>
      </section>

      <section className="bg-white rounded-lg shadow border border-slate-200 p-5 mb-5">
        <h2 className="font-semibold mb-4">Logo</h2>
        <div className="flex items-center gap-4">
          {owner.has_logo && (
            <img src={`/api/admin/owners/${owner.id}/logo?t=${Date.now()}`} alt="" className="h-20 max-w-[200px] object-contain bg-slate-50 border border-slate-200 rounded" />
          )}
          <label className="bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded px-3 py-2 text-sm cursor-pointer">
            {savingLogo ? 'Laster opp …' : (owner.has_logo ? 'Bytt logo' : 'Last opp logo')}
            <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
          </label>
        </div>
      </section>

      <section className="bg-white rounded-lg shadow border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold">Vipps-avtaler</h2>
          {melding && <span className={melding.ok ? 'text-green-700 text-sm' : 'text-red-700 text-sm'}>{melding.tekst}</span>}
          <button onClick={() => setCreatingVipps((v) => !v)} className="flex items-center gap-2 bg-brand hover:bg-brand-dark text-white rounded px-3 py-1.5 text-sm font-medium">
            <Plus size={14} /> Ny avtale
          </button>
        </div>

        {creatingVipps && (
          <form onSubmit={addVipps} className="grid grid-cols-2 gap-4 mb-5 bg-slate-50 p-4 rounded">
            <Field label="Nøkler">
              <select className="input" value={vippsForm.auth_mode} onChange={(e) => setVippsForm((f) => ({ ...f, auth_mode: e.target.value as ChannelAuthMode }))}>
                <option value="PARTNER">Partnernøkler (Østerhus) + MSN — bare produksjon</option>
                <option value="MERCHANT">Salgsstedets egne nøkler (også testmiljøet)</option>
              </select>
            </Field>
            <Field label="MSN (Merchant Serial Number)">
              <input className="input" value={vippsForm.msn} onChange={(e) => setVippsForm((f) => ({ ...f, msn: e.target.value }))} required />
            </Field>
            <Field label="Miljø">
              <select className="input" value={vippsForm.environment} onChange={(e) => setVippsForm((f) => ({ ...f, environment: e.target.value as 'test' | 'prod' }))}>
                <option value="test">Test</option>
                <option value="prod">Produksjon</option>
              </select>
            </Field>
            {vippsForm.auth_mode === 'MERCHANT' && (
              <>
                <Field label="Client ID">
                  <input className="input" value={vippsForm.client_id} onChange={(e) => setVippsForm((f) => ({ ...f, client_id: e.target.value }))} required />
                </Field>
                <Field label="Client Secret">
                  <input className="input" type="password" autoComplete="off" value={vippsForm.client_secret} onChange={(e) => setVippsForm((f) => ({ ...f, client_secret: e.target.value }))} required />
                </Field>
                <Field label="Subscription Key">
                  <input className="input" type="password" autoComplete="off" value={vippsForm.subscription_key} onChange={(e) => setVippsForm((f) => ({ ...f, subscription_key: e.target.value }))} required />
                </Field>
              </>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={vippsForm.allow_push} onChange={(e) => setVippsForm((f) => ({ ...f, allow_push: e.target.checked }))} />
              Push er godkjent av Vipps for salgsstedet
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={vippsForm.allow_qr} onChange={(e) => setVippsForm((f) => ({ ...f, allow_qr: e.target.checked }))} />
              QR-kode kan brukes
            </label>
            <div className="col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setCreatingVipps(false)} className="px-3 py-2 text-sm">Avbryt</button>
              <button type="submit" className="bg-brand hover:bg-brand-dark text-white rounded px-3 py-2 text-sm font-medium">
                Lagre
              </button>
            </div>
          </form>
        )}

        <table className="w-full text-sm">
          <thead className="text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left py-2">MSN</th>
              <th className="text-left py-2">Miljø</th>
              <th className="text-left py-2">Nøkler</th>
              <th className="text-left py-2">Push</th>
              <th className="text-left py-2">QR</th>
              <th className="text-left py-2">Webhook</th>
              <th className="text-left py-2">Aktiv</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {channels.map((c) => (
              <tr key={c.id} className="border-t border-slate-100">
                <td className="py-2 font-mono">{c.msn}</td>
                <td className="py-2">{c.environment}</td>
                <td className="py-2">{c.auth_mode === 'PARTNER' ? 'Partner' : 'Egne'}</td>
                <td className="py-2">
                  <input type="checkbox" checked={c.allow_push} title="Push godkjent av Vipps" onChange={(e) => void kanalHandling('Lagret', () => api.patch(`/api/admin/channels/${c.id}`, { allow_push: e.target.checked }))} />
                </td>
                <td className="py-2">
                  <input type="checkbox" checked={c.allow_qr} title="QR-kode" onChange={(e) => void kanalHandling('Lagret', () => api.patch(`/api/admin/channels/${c.id}`, { allow_qr: e.target.checked }))} />
                </td>
                <td className="py-2 text-xs">
                  {c.webhook_registered_at ? new Date(c.webhook_registered_at).toLocaleString('nb-NO') : <span className="text-amber-700">ikke registrert</span>}
                </td>
                <td className="py-2">
                  <input type="checkbox" checked={c.active} title="Aktiv" onChange={(e) => void kanalHandling('Lagret', () => api.patch(`/api/admin/channels/${c.id}`, { active: e.target.checked }))} />
                </td>
                <td className="py-2 text-right whitespace-nowrap">
                  <button onClick={() => void kanalHandling('Vipps godtok nøklene', () => api.post(`/api/admin/channels/${c.id}/test`))} className="text-slate-600 hover:text-slate-900 mr-3" title="Test tilkobling mot Vipps">
                    <PlugZap size={16} />
                  </button>
                  <button onClick={() => void kanalHandling('Webhook registrert hos Vipps', () => api.post(`/api/admin/channels/${c.id}/webhook`))} className="text-slate-600 hover:text-slate-900 mr-3" title="Registrer webhook hos Vipps">
                    <Webhook size={16} />
                  </button>
                  <button onClick={() => deleteVipps(c.id)} className="text-red-600 hover:text-red-800" title="Slett avtale">
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {channels.length === 0 && (
              <tr>
                <td colSpan={8} className="py-4 text-center text-slate-500 text-sm">
                  Ingen Vipps-avtaler. Legg til én før eieren kan ta imot betalinger.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <style>{`.input { width:100%; border:1px solid #cbd5e1; border-radius:4px; padding:6px 10px; }`}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">{label}</span>
      {children}
    </label>
  );
}
